import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import moment from "moment";
import { useEffect, useState } from 'react';
import { Else, If, Then } from 'react-if';
import { Link } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import Badge from "reactstrap/lib/Badge";
import Button from "reactstrap/lib/Button";
import Col from "reactstrap/lib/Col";
import Container from "reactstrap/lib/Container";
import Row from "reactstrap/lib/Row";
import Table from "reactstrap/lib/Table";
import { useAdminStore } from '~/components/use-store';

function Exam() {
  const adminStore = useAdminStore();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [examsPerPage, setExamsPerPage] = useState(10); // Default 10 exams per page
  
  const [prepareDelete, setPrepareDelete] = useState({})
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    adminStore.examFetch();
    return () => { };
  }, [adminStore])

  function handleDelete() {
    setIsDeleting(true);
    adminStore.examDelete(prepareDelete._id).then(() => {
      setIsDeleting(false);
      setPrepareDelete({});
    })
  }

  // Pagination logic
  const totalExams = adminStore.exams.length;
  const totalPages = Math.ceil(totalExams / examsPerPage);
  const indexOfLastExam = currentPage * examsPerPage;
  const indexOfFirstExam = indexOfLastExam - examsPerPage;
  const currentExams = adminStore.exams.slice(indexOfFirstExam, indexOfLastExam);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleExamsPerPageChange = (e) => {
    setExamsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Show: </span>
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto' }}
            value={examsPerPage} 
            onChange={handleExamsPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="ms-2"> entries</span>
        </div>

        <div className="d-flex align-items-center">
          <Button
            color="outline-secondary"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="me-2"
          >
            Previous
          </Button>

          {startPage > 1 && (
            <>
              <Button
                color="outline-primary"
                size="sm"
                onClick={() => handlePageChange(1)}
                className="me-1"
              >
                1
              </Button>
              {startPage > 2 && <span className="me-2">...</span>}
            </>
          )}

          {pageNumbers.map(number => (
            <Button
              key={number}
              color={currentPage === number ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => handlePageChange(number)}
              className="me-1"
            >
              {number}
            </Button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="me-2">...</span>}
              <Button
                color="outline-primary"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                className="me-1"
              >
                {totalPages}
              </Button>
            </>
          )}

          <Button
            color="outline-secondary"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="ms-1"
          >
            Next
          </Button>
        </div>

        <div className="text-muted">
          <small>
            Showing {indexOfFirstExam + 1} to {Math.min(indexOfLastExam, totalExams)} of {totalExams} entries
          </small>
        </div>
      </div>
    );
  };

  return (
    <Container className="pb-4">
      <Row className="mt-4">
        <Col>
          <h1>Ujian</h1>
        </Col>
        <Col>
          <div className="text-right">
            <Button color="primary" size="lg" tag={Link} to="/admin/exam/new">Create New</Button>
          </div>
        </Col>
      </Row>
      
      {/* Summary Row */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">Total: {totalExams} exam{totalExams !== 1 ? 's' : ''}</span>
            <span className="text-muted">Page {currentPage} of {totalPages}</span>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Table striped>
            <thead>
              <tr>
                <th>Lecture Info</th>
                <th>Shift</th>
                <th colSpan={2}>Duration (Start-End)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {currentExams.map((exam, i) =>
                <tr key={exam._id || i}>
                  <td>
                    <b>{(exam.lecture || {}).name} ({(exam.lecture || {}).lecture_code})</b> <br />
                    {(exam.lecture_period || {}).period_code} <Badge color={parseInt(exam.uts) ? (parseInt(exam.uts) === 1 ? "success" : "primary") : "info"}>{parseInt(exam.uts) ? (parseInt(exam.uts) === 1 ? "UTS" : "QUIZ") : "UAS"}</Badge>
                    <Badge className="mx-2" color={exam.online ? "warning" : "secondary"}>{exam.online ? "ONLINE" : "OFFLINE"}</Badge>
                  </td>
                  <td>
                    <p className="h3">
                      <If condition={exam.shift === null}>
                        <Then>-</Then>
                        <Else>{exam.shift}</Else>
                      </If>
                    </p>
                  </td>
                  <td>
                    {exam.time_start} - {exam.time_ended}
                  </td>
                  <td>
                    Durasi: {exam.time_duration / 60} Menit<br />
                    Dimulai pada: {exam.time_opened || "(belum di mulai)"}
                  </td>
                  <td className="text-right">
                    <Button className="mx-2 d-none d-md-block" color="warning" tag={Link} to={`/admin/exam/${exam._id}/detail`}>Lihat</Button>
                    <Button className="mx-2 d-block d-md-none" color="warning" tag={Link} to={`/admin/exam/${exam._id}/minipanel`}>Lihat</Button>
                    <Button className="mx-2" color="danger" onClick={() => setPrepareDelete(exam)}>Hapus</Button>
                  </td>
                </tr>
              )}
              {currentExams.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-4">
                    No exams found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          
          {/* Pagination Controls */}
          {renderPagination()}
        </Col>
      </Row>

      <Modal isOpen={!!(prepareDelete._id)} toggle={() => setPrepareDelete({})} backdrop>
        <ModalHeader toggle={() => setPrepareDelete({})}>Konfirmasi Hapus</ModalHeader>
        <ModalBody>
          <h4>Are you sure to delete this exam?</h4>
          <h3>#{prepareDelete._id} <Badge color={prepareDelete.uts ? "info" : "success"}>{prepareDelete.uts ? "UTS" : "UAS"}</Badge> {prepareDelete.lecture?.name} ({(prepareDelete.lecture || {}).lecture_code})</h3>
          <p className="lead"> {(prepareDelete.shift === 0) ? "Shiftless" : ("Shift " + prepareDelete.shift)} | {moment(prepareDelete.time_start).format("LLLL")} | {prepareDelete.duration / 60} Menit.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPrepareDelete({})}>Cancel</Button>{' '}
          <Button color="danger" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} /> Hapus{isDeleting ? <Spinner as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          /> : ""

          }</Button>
        </ModalFooter>
      </Modal>

    </Container>)
}

export default observer(Exam)